import { getSubdomain } from "../../utils/getSubdomain/subdomain";
import { createApiClient } from "../apiService";
import {
  AddFileToReviewTypes,
  createReviewerTypes,
  DeleteFileFromReviewTypes,
  editReviewerTypes,
  getReviewBySlugAndReviewerTypes,
  reviewerTypes,
  reviewTypes,
  ServiceReviewtypes,
  SuccessCertificateType,
  SuccesType,
} from "./reviewTypes";
import { useToken } from "../tokenUtils";

const apiClient = createApiClient("/");
const domain = getSubdomain();

const handleRequest = async (
  path: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.post(path, data, { headers });
  return res.data;
};

const handlePatchRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.patch(url, data, { headers });
  return res.data;
};

const handlePutRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };
  const res = await apiClient.put(url, data, { headers });
  return res.data;
};

const handleDeleteRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.request({
    url,
    method: "DELETE",
    headers,
    data,
  });

  return res.data;
};

const getReviewers = async (): Promise<reviewerTypes[]> => {
  const token = useToken();
  const res = await apiClient.get(`${domain}/reviewer/`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return res.data as reviewerTypes[];
};

const getReviewersForArticle = async (
  slug: string,
): Promise<reviewerTypes[]> => {
  const token = useToken();
  const res = await apiClient.get(`${domain}/reviewer/${slug}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return res.data as reviewerTypes[];
};

const createReviewer = async (
  data: createReviewerTypes,
): Promise<SuccesType> => {
  const token = useToken();
  const res = await handleRequest(`${domain}/reviewer/`, data, token);
  return res as SuccesType;
};

const getReviewersBySlug = async (slug: string): Promise<reviewerTypes[]> => {
  const token = useToken();
  const res = await handleGetRequest(
    apiClient,
    `${domain}/reviewer/slug/${slug}`,
    token,
  );
  return res as reviewerTypes[];
};

const editReviewer = async (data: editReviewerTypes): Promise<SuccesType> => {
  const token = useToken();
  const res = await handlePatchRequest(
    `/${domain}/reviewer/${data.slug}`,
    data,
    token,
  );
  return res as SuccesType;
};

const getMyPendingReviewsByStatus = async (
  status: number[],
): Promise<reviewTypes[]> => {
  const token = useToken();
  if (!token) {
    throw new Error(
      "Tokens de autenticación faltantes: verifica 'token' y 'token'",
    );
  }
  const res = await apiClient.get(`${domain}/review/me?status=${status}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return res.data as reviewTypes[];
};

const assignReview = async (data: ServiceReviewtypes): Promise<SuccesType> => {
  if (!data.article_slug) {
    throw new Error("Faltan campos requeridos para construir el path");
  }
  const token = useToken();
  if (!token) {
    throw new Error("Faltan token se debe iniciar session ");
  }
  const path = `${domain}/review/${data.article_slug}/assign_reviewer/}`;
  const res = await handleRequest(path, data, token);
  return res as SuccesType;
};

const acceptReview = async (data: ServiceReviewtypes): Promise<SuccesType> => {
  if (!data.article_slug) {
    throw new Error("Faltan campos requeridos para construir el path");
  }
  const token = useToken();
  if (!token) {
    throw new Error("Faltan token se debe iniciar session ");
  }
  const path = `${domain}/review/${data.article_slug}/accept`;
  const res = await handlePutRequest(path, {}, token);
  return res as SuccesType;
};

const rejectReview = async (data: ServiceReviewtypes): Promise<SuccesType> => {
  if (!data.article_slug) {
    throw new Error("Faltan campos requeridos para construir el path");
  }
  const token = useToken();

  const path = `${domain}/review/${data.article_slug}/reject`;
  const res = await handlePutRequest(path, data, token);
  return res as SuccesType;
};

const sendReview = async (data: ServiceReviewtypes): Promise<SuccessCertificateType> => {
  if (!data.article_slug) {
    throw new Error("Faltan campos requeridos para construir el path");
  }
  const token = useToken();

  const path = `${domain}/review/${data.article_slug}/send`;
  const res = await handlePutRequest(path, data, token);
  return res as SuccessCertificateType;
};

const handlePostFileRequest = async (
  url: string,
  data: any,
  token?: string,
): Promise<any> => {
  const headers: Record<string, string> = {
    Accept: "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };

  const res = await apiClient.post(url, data, { headers });
  return res.data;
};

const addFileToReview = async (
  data: AddFileToReviewTypes,
): Promise<SuccesType> => {
  const token = useToken();
  const path = `${domain}/review/${data.article_slug}/add_file`;
  const res = await handlePostFileRequest(path, data.file, token);
  return res as SuccesType;
};

const deleteFileFromReview = async (
  data: DeleteFileFromReviewTypes,
): Promise<SuccesType> => {
  const token = useToken();
  const path = `${domain}/review/${data.article_slug}/delete_file?filename=${encodeURIComponent(data.filename)}`;
  const res = await handleDeleteRequest(path, {}, token);
  return res as SuccesType;
};

const getMyReviewsByStatus = async (status: number): Promise<reviewTypes[]> => {
  const token = useToken();
  if (!token) {
    throw new Error(
      "Tokens de autenticación faltantes: verifica 'token' y 'token'",
    );
  }
  const res = await apiClient.get(`${domain}/review/me/${status}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return res.data as reviewTypes[];
};
const deleteReviewer = async (name: string): Promise<SuccesType> => {
  const token = useToken();
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
  };
  const res = await apiClient.delete(`${domain}/reviewer/${name}/`, {
    headers,
  });
  return res.data as SuccesType;
};

const getPendingReviews = async (): Promise<reviewTypes[]> => {
  const token = useToken();
  if (!token) {
    throw new Error(
      "Tokens de autenticación faltantes: verifica 'token' y 'token'",
    );
  }
  const res = await apiClient.get(`${domain}/review/pending/me`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return res.data as reviewTypes[];
};

export const handleGetRequest = async <T>(
  apiClient: any,
  url: string,
  token: string | null,
): Promise<T> => {
  const res = await apiClient.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return res.data as T;
};

const getReviewBySlug = async (slug: string): Promise<reviewTypes> => {
  const token = useToken();
  if (!slug) {
    throw new Error("Slug parameter is required");
  }

  const response = await handleGetRequest(
    apiClient,
    `${domain}/review_by_slug/${slug}`,
    token,
  );
  return response as reviewTypes;
};

const getReviewBySlugAndReviewer = async (
  data: getReviewBySlugAndReviewerTypes,
): Promise<reviewTypes> => {
  const token = useToken();
  if (!data.slug) {
    throw new Error("Slug parameter is required");
  }
  const response = await handleGetRequest(
    apiClient,
    `${domain}/review_by_slug/${data.slug}/reviewer/${data.version}`,
    token,
  );
  return response as reviewTypes;
};

const getReviewBySlugByAuthor = async (slug: string): Promise<reviewTypes> => {
  const token = useToken();
  if (!slug) {
    throw new Error("Slug parameter is required");
  }

  const response = await handleGetRequest(
    apiClient,
    `${domain}/review/${slug}`,
    token,
  );
  return response as reviewTypes;
};

const getMyCompletedReviews = async (): Promise<reviewTypes[]> => {
  const token = useToken();
  if (!token) {
    throw new Error(
      "Tokens de autenticación faltantes: verifica 'token' y 'token'",
    );
  }
  const res = await apiClient.get(`${domain}/review/me/completed`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return res.data as reviewTypes[];
};
const getMyclosedReviews = async (): Promise<reviewTypes[]> => {
  const token = useToken();
  if (!token) {
    throw new Error(
      "Tokens de autenticación faltantes: verifica 'token' y 'token'",
    );
  }
  const res = await apiClient.get(`${domain}/review/me/closed`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return res.data as reviewTypes[];
};
const getCertificateInfo = async (idReview: number): Promise<reviewTypes[]> => {
  const token = useToken();
  if (!token) {
    throw new Error(
      "Tokens de autenticación faltantes: verifica 'token' y 'token'",
    );
  }
  const res = await apiClient.get(
    `${domain}/review/me/${idReview}/certificate`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return res.data as reviewTypes[];
};

const getPublicCertificateInfo = async (
  idReview: number,
): Promise<reviewTypes[]> => {
  const res = await apiClient.get(
    `${domain}/review/me/${idReview}/certificate/public`,
  );
  return res.data as reviewTypes[];
};

const generateCertificate = async (id_review: number): Promise<SuccesType> => {
  const token = useToken();
  const res = await handlePatchRequest(
    `/${domain}/review/me/${id_review}/generate_certificate`,
    {},
    token,
  );
  return res as SuccesType;
};

const getByCertificateCode = async (
  certificateCode: string,
  token?: string | null,
): Promise<reviewTypes[]> => {
  if (!certificateCode) {
    throw new Error("certificateCode es requerido");
  }

  const res = await apiClient.get(
    `${domain}/review/me/${certificateCode}/certificate_code`,
    {
      params: token ? { token } : undefined,
    },
  );
  return res.data as reviewTypes[];
};

const reviewService = {
  getReviewers,
  getReviewersForArticle,
  createReviewer,
  editReviewer,
  getMyPendingReviewsByStatus,
  getMyCompletedReviews,
  getMyclosedReviews,
  assignReview,
  acceptReview,
  rejectReview,
  sendReview,
  addFileToReview,
  deleteFileFromReview,
  getMyReviewsByStatus,
  deleteReviewer,
  getReviewBySlug,
  getReviewBySlugByAuthor,
  getReviewersBySlug,
  getReviewBySlugAndReviewer,
  getPendingReviews,
  getCertificateInfo,
  generateCertificate,
  getByCertificateCode,
  getPublicCertificateInfo,
};

export default reviewService;
